import "./DealWorkflow.css";

import { useContext } from "react";

import { ArrowheadLeftLightIcon, DealArrowsIcon } from "../../../../assets";
import { DealJourneyNavigatorPages, TemplateConstants } from "../../../../constants";
import {
  Button,
  Column,
  DatePicker,
  Divider,
  ErrorBox,
  FullPageCard,
  KanaTooltip,
  Row,
  Select,
} from "../../../../widget";
import { NewDealJourneyContext } from "../NewDealJourneyContext";
import { useDealWorkflow } from "./useDealWorkflow";
import { getKeyDateLabel, getStageTitle } from "./utils/dealWorkflowUtils";

const getPreviousPage = (currentPage: DealJourneyNavigatorPages): DealJourneyNavigatorPages => {
  const index = Object.values(DealJourneyNavigatorPages).findIndex((page) => page === currentPage);

  return Object.values(DealJourneyNavigatorPages)[index - 1];
};

export const DealWorkflow = (): JSX.Element => {
  const { currentPage, handlePageChange, handleCreateDeal } = useContext(NewDealJourneyContext);
  const {
    currentPageErrors,
    formValues,
    handleStageDateChange,
    handleWorkflowTemplateChange,
    isLoadingWorkflowTemplate,
    workflowTemplateOptions,
    orderedDealPhases,
  } = useDealWorkflow();

  return (
    <div className="DealWorkflowContainer">
      <FullPageCard corners="rounded" padding="ll">
        <div className="DealWorkflowCard">
          <Row spacingV="ml">
            <Column span={12}>
              <h2>Workflow</h2>
            </Column>
          </Row>
          <Row spacingV="xl">
            <Column span={12}>
              <p className="body1">
                Choose a workflow template to enable start and end dates for phases of your deal. If you would rather
                apply a workflow later, select &apos;Create deal&apos; to proceed.
              </p>
            </Column>
          </Row>
          {currentPageErrors && currentPageErrors.length > 0 && (
            <Row spacingV="ml">
              <Column span={10}>
                <ErrorBox text={currentPageErrors.map((error) => error.message)} />
              </Column>
            </Row>
          )}
          <Row spacingV="ll">
            <Column span={5}>
              <div className="DealWorkflowTemplateControls">
                <div className="DealWorkflowTemplateSelect">
                  <Select
                    label="Workflow template"
                    placeholder="Select"
                    data={workflowTemplateOptions}
                    value={formValues.dealWorkflowTemplateUuid}
                    onChange={handleWorkflowTemplateChange}
                    disabled={isLoadingWorkflowTemplate}
                    corners="rounded"
                  />
                </div>
                {formValues.dealWorkflowTemplateUuid && (
                  <Button
                    text="Clear"
                    variant="tertiary"
                    onClick={() => handleWorkflowTemplateChange("")}
                    disabled={isLoadingWorkflowTemplate}
                    corners="rounded"
                    size="small"
                  />
                )}
              </div>
            </Column>
          </Row>
          {orderedDealPhases.length > 0 && (
            <div className="DealWorkflowStages">
              {orderedDealPhases.map((phaseItem) => (
                <div key={phaseItem.phase} className="DealWorkflowStageSection">
                  <Divider type="horizontal" />
                  <Row spacingV="l" className="DealWorkflowStageHeaderRow">
                    <Column span={12}>
                      <p className="body01">{getStageTitle(phaseItem.phase)}</p>
                    </Column>
                  </Row>
                  <Row spacingV="m">
                    {phaseItem.keyDates.map((keyDate, keyDateIndex) => (
                      <Column
                        key={`${phaseItem.phase}-${keyDate}`}
                        span="4_half"
                        offset={keyDateIndex % 2 === 1 ? 1 : 0}
                        className="DealWorkflowDatePicker"
                      >
                        <DatePicker
                          label={getKeyDateLabel(keyDate)}
                          value={formValues.dealWorkflowStageDates[phaseItem.phase]?.[keyDate]}
                          onChange={(value) => handleStageDateChange(phaseItem.phase, keyDate, value)}
                          corners="rounded"
                          tooltip={
                            <KanaTooltip tooltipHeader="" tooltipText={TemplateConstants.DEAL_KEY_DATE_TOOLTIP_TEXT} />
                          }
                        />
                      </Column>
                    ))}
                  </Row>
                </div>
              ))}
            </div>
          )}
          <Row>
            <Column span={12}>
              <div className="DealWorkflowButtons">
                {currentPage !== DealJourneyNavigatorPages.DETAILS && (
                  <Button
                    text="Previous"
                    variant="tertiary"
                    hasNoPadding
                    icon={<ArrowheadLeftLightIcon className="ArrowheadLeftBlueIcon" />}
                    iconPosition="left"
                    onClick={() => handlePageChange(getPreviousPage(currentPage))}
                    corners="rounded"
                    size="smallLarge"
                  />
                )}
                <Button
                  text="Create deal"
                  variant="primary"
                  hasNoPadding
                  icon={<DealArrowsIcon className="DealArrowsIcon" />}
                  onClick={handleCreateDeal}
                  corners="rounded"
                  size="smallLarge"
                />
              </div>
            </Column>
          </Row>
        </div>
      </FullPageCard>
    </div>
  );
};
